'use client'

import { Cell, Spacer, Text } from '@vinted/web-ui'
import { useState } from 'react'
import { useFormContext } from 'react-hook-form'

import {
  DocumentFieldConfigurationModel,
  PaymentsIdentityDocumentConfigurationModel,
  PaymentsIdentityFileModel,
} from '@marketplace-web/pay-identity/payments-identity-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { PhotoTips } from '@marketplace-web/item-upload/photo-tips-feature'
import {
  EntryType,
  getPhotoTips,
  PhotoTipModel,
  transformPhotoTipDtos,
} from '@marketplace-web/item-upload/photo-tips-data'
import {
  renderValidation,
  useFormValidationMessage,
} from '@marketplace-web/vendor-abstractions/react-hook-form-feature'
import { KybFormFields } from '@marketplace-web/pay-identity/pay-identity-kyc-data'

import ChangeDocumentTypeConfirmModal from './ChangeDocumentTypeConfirmModal'
import DocumentSelector from './DocumentSelector'
import TypeSelector from './TypeSelector'

type Props = {
  identityDocumentConfiguration?: DocumentFieldConfigurationModel
}

const IdentityDocument = ({ identityDocumentConfiguration }: Props) => {
  const translate = useTranslate('kyc_form.identity_document')
  const kybTranslate = useTranslate('business.kyb')
  const {
    setError,
    getValues,
    formState: { errors },
  } = useFormContext()
  const validationMessage = useFormValidationMessage(errors, 'kyc_form')

  const [isChangeDocumentTypeConfirmModalVisible, setIsChangeDocumentTypeConfirmModalVisible] =
    useState(false)
  const [photoTips, setPhotoTips] = useState<Array<PhotoTipModel>>([])
  const [isPhotoTipsModalOpen, setIsPhotoTipsModalOpen] = useState(false)

  const [selectedConfiguration, setSelectedConfiguration] =
    useState<PaymentsIdentityDocumentConfigurationModel | null>(
      identityDocumentConfiguration?.options[0] || null,
    )

  const [preSelectedConfiguration, setPreSelectedConfiguration] =
    useState<PaymentsIdentityDocumentConfigurationModel | null>(null)

  const getIdentityDocument = () => {
    return (getValues(KybFormFields.IdentityDocument) as PaymentsIdentityFileModel) || null
  }

  const fetchPhotoTips = async () => {
    if (photoTips.length > 0) return

    const response = await getPhotoTips(EntryType.IdentityDocument)

    if ('errors' in response) return

    setPhotoTips(transformPhotoTipDtos(response.photo_tips))
  }

  const handlePhotoTipsClick = () => {
    fetchPhotoTips()
    setIsPhotoTipsModalOpen(true)
  }

  const closePhotoTipsModal = () => {
    setIsPhotoTipsModalOpen(false)
  }

  const handleTypeChange = (configuration: PaymentsIdentityDocumentConfigurationModel) => {
    const identityDocument = getIdentityDocument()

    const files = identityDocument?.files?.map(file => file.body).filter(body => !!body)

    if (files.length) {
      setIsChangeDocumentTypeConfirmModalVisible(true)
      setPreSelectedConfiguration(configuration)

      return
    }

    setSelectedConfiguration(configuration)
  }

  const handleDocumentSelectorError = (error: string) => {
    setError(KybFormFields.IdentityDocument, { type: 'manual', message: error })
  }

  const handleChangeDocumentTypeConfirm = () => {
    setSelectedConfiguration(preSelectedConfiguration)
    setIsChangeDocumentTypeConfirmModalVisible(false)
  }

  const handleChangeDocumentTypeCancel = () => {
    setPreSelectedConfiguration(null)
    setIsChangeDocumentTypeConfirmModalVisible(false)
  }

  const renderChangeDocumentTypeConfirmModal = () => {
    return (
      <ChangeDocumentTypeConfirmModal
        show={isChangeDocumentTypeConfirmModalVisible}
        onConfirm={handleChangeDocumentTypeConfirm}
        onCancel={handleChangeDocumentTypeCancel}
      />
    )
  }

  const renderTitle = () => (
    <Cell
      styling={Cell.Styling.Wide}
      body={
        <Text as="h3" type={Text.Type.Subtitle} text={kybTranslate('identity_document.title')} />
      }
    />
  )

  const renderTypeSelector = () => {
    if (!identityDocumentConfiguration) return null

    return (
      <TypeSelector
        options={identityDocumentConfiguration.options}
        selectedConfiguration={selectedConfiguration}
        onChange={handleTypeChange}
      />
    )
  }

  const renderPhotoTips = () => {
    return (
      <>
        <Cell
          styling={Cell.Styling.Tight}
          body={
            <>
              <Text type={Text.Type.Subtitle} text={translate('upload_document.title')} as="span" />
              <Spacer orientation={Spacer.Orientation.Vertical} />
              <button type="button" onClick={handlePhotoTipsClick}>
                <Text
                  type={Text.Type.Subtitle}
                  text={`${translate('upload_document.upload_tips')}`}
                  width={Text.Width.Parent}
                  format
                  inline
                  clickable
                  as="span"
                />
              </button>
            </>
          }
        />
        <PhotoTips
          isBodyHtml
          isOpen={isPhotoTipsModalOpen}
          photoTips={photoTips}
          nextButtonText={translate('photo_tips.next_tip')}
          backButtonText={translate('photo_tips.previous_tip')}
          handleClose={closePhotoTipsModal}
        />
      </>
    )
  }

  const renderDocumentUploader = () => {
    if (!selectedConfiguration) return null

    return (
      <Cell
        styling={Cell.Styling.Wide}
        validation={renderValidation(validationMessage(KybFormFields.IdentityDocument))}
      >
        {renderPhotoTips()}
        <Spacer size={Spacer.Size.XLarge} />
        <DocumentSelector
          configuration={selectedConfiguration}
          onError={handleDocumentSelectorError}
        />
      </Cell>
    )
  }

  if (!identityDocumentConfiguration) return null

  return (
    <>
      {renderTitle()}
      {renderTypeSelector()}
      {renderDocumentUploader()}
      {renderChangeDocumentTypeConfirmModal()}
    </>
  )
}

export default IdentityDocument
